import './style.css';
import { toPng } from 'html-to-image';


interface RateConfig {
  callMinuteUsd: number;
  giftedMinuteUsd: number;
  modelSplit: number;
  defaultUsdCop: number;
}

interface Goal {
  key: string;
  name: string;
  minutesDay: number;
  daysWeek: number;
  giftWeek: number;
}

interface AppState {
  usdCop: number;
  liveRateSource: 'en vivo' | 'referencia' | 'referencia base';
  liveRateUpdatedAt: string | null;
}

interface CalculationInput {
  minutesDay: number;
  daysWeek: number;
  giftWeek: number;
  usdCop: number;
}

interface CalculationResult extends CalculationInput {
  calledWeek: number;
  grossWeekUsd: number;
  modelDayUsd: number;
  modelWeekUsd: number;
  modelMonthUsd: number;
  modelDayCop: number;
  modelWeekCop: number;
  modelMonthCop: number;
}

const RATES: RateConfig = {
  callMinuteUsd: 0.26,
  giftedMinuteUsd: 0.10, // Updated from 0.01 to 0.10 based on new HTML
  modelSplit: 0.50,
  defaultUsdCop: 3400
};

const GOALS: Goal[] = [
  { key: 'inicio', name: 'Meta inicio', minutesDay: 180, daysWeek: 5, giftWeek: 120 },
  { key: 'base', name: 'Meta base recomendada', minutesDay: 250, daysWeek: 5, giftWeek: 250 },
  { key: 'constante', name: 'Meta constante', minutesDay: 300, daysWeek: 5, giftWeek: 250 },
  { key: 'fuerte', name: 'Meta fuerte', minutesDay: 350, daysWeek: 6, giftWeek: 300 },
  { key: 'top', name: 'Meta top', minutesDay: 450, daysWeek: 6, giftWeek: 450 }
];

const state: AppState = {
  usdCop: RATES.defaultUsdCop,
  liveRateSource: 'referencia base',
  liveRateUpdatedAt: null
};

const els = {
  goal: document.getElementById('goal') as HTMLSelectElement,
  minutesDay: document.getElementById('minutesDay') as HTMLInputElement,
  giftsDay: document.getElementById('giftsDay') as HTMLInputElement, // New field added
  daysWeek: document.getElementById('daysWeek') as HTMLInputElement,
  giftWeek: document.getElementById('giftWeek') as HTMLInputElement,
  rateDisplay: document.getElementById('rateDisplay') as HTMLElement,
  scenarioText: document.getElementById('scenarioText') as HTMLElement,
  summaryNote: document.getElementById('summaryNote') as HTMLElement | null, // Optional now
  modelDayUsd: document.getElementById('modelDayUsd') as HTMLElement,
  modelDayCop: document.getElementById('modelDayCop') as HTMLElement,
  modelWeekUsd: document.getElementById('modelWeekUsd') as HTMLElement,
  modelWeekCop: document.getElementById('modelWeekCop') as HTMLElement,
  modelMonthUsd: document.getElementById('modelMonthUsd') as HTMLElement,
  modelMonthCop: document.getElementById('modelMonthCop') as HTMLElement,
  goalsTable: document.getElementById('goalsTable') as HTMLElement,
  exportImage: document.getElementById('exportImage') as HTMLButtonElement,
  exportPdf: document.getElementById('exportPdf') as HTMLButtonElement,
  resetBtn: document.getElementById('resetBtn') as HTMLButtonElement,
  toast: document.getElementById('toast') as HTMLElement
};

function num(value: string | number, fallback = 0): number {
  const parsed = Number(String(value).replace(/,/g, '.'));
  return Number.isFinite(parsed) ? parsed : fallback;
}

function whole(value: number): string {
  return new Intl.NumberFormat('es-CO', { maximumFractionDigits: 0 }).format(value);
}

function moneyUsd(value: number): string {
  const v = Number(value || 0);
  const formatted = v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `$${formatted} USD`;
}

function moneyCop(value: number): string {
  const v = Math.round(Number(value || 0));
  return `COP ${v.toLocaleString('es-CO')}`;
}

function showToast(text: string): void {
  els.toast.textContent = text;
  els.toast.classList.add('show');
  window.setTimeout(() => els.toast.classList.remove('show'), 2100);
}

function getInputs(): CalculationInput {
  const minutesDay = Math.max(0, num(els.minutesDay.value));
  const daysWeek = Math.min(7, Math.max(1, num(els.daysWeek.value, 5)));
  const giftsDay = Math.max(0, num(els.giftsDay.value));
  
  // Calculate giftWeek based on daily gifted minutes and days worked per week
  const giftWeek = giftsDay * daysWeek;
  
  // Keep the readonly input field in sync with the calculated value
  els.giftWeek.value = String(giftWeek);

  return {
    minutesDay,
    daysWeek,
    giftWeek,
    usdCop: state.usdCop
  };
}

function calculate(input: CalculationInput): CalculationResult {
  const calledWeek = input.minutesDay * input.daysWeek;
  const grossCallWeekUsd = calledWeek * RATES.callMinuteUsd;
  const grossGiftWeekUsd = input.giftWeek * RATES.giftedMinuteUsd;
  const grossWeekUsd = grossCallWeekUsd + grossGiftWeekUsd;
  const modelWeekUsd = grossWeekUsd * RATES.modelSplit;
  const modelDayUsd = input.daysWeek ? modelWeekUsd / input.daysWeek : 0;
  const modelMonthUsd = modelWeekUsd * 4;
  return {
    ...input,
    calledWeek,
    grossWeekUsd,
    modelDayUsd,
    modelWeekUsd,
    modelMonthUsd,
    modelDayCop: modelDayUsd * input.usdCop,
    modelWeekCop: modelWeekUsd * input.usdCop,
    modelMonthCop: modelMonthUsd * input.usdCop
  };
}

function renderSummary(): CalculationResult {
  const c = calculate(getInputs());
  els.scenarioText.innerHTML = `<strong>${whole(c.minutesDay)} minutos por día · ${whole(c.giftWeek)} minutos regalados por semana · ${whole(c.daysWeek)} días por semana.</strong>`;
  els.modelDayCop.textContent = moneyCop(c.modelDayCop);
  els.modelDayUsd.textContent = moneyUsd(c.modelDayUsd);
  els.modelWeekCop.textContent = moneyCop(c.modelWeekCop);
  els.modelWeekUsd.textContent = moneyUsd(c.modelWeekUsd);
  els.modelMonthCop.textContent = moneyCop(c.modelMonthCop);
  els.modelMonthUsd.textContent = moneyUsd(c.modelMonthUsd);
  if (els.summaryNote) {
    els.summaryNote.innerHTML = `<strong>Tasa de conversión:</strong> ${whole(state.usdCop)} COP por USD. <strong>Tarifas:</strong> USD 0.07 el primer minuto, USD 0.26 por minuto adicional en llamada y USD 0.10 por minuto regalado. <strong>Mes estimado:</strong> 4 semanas.`;
  }
  renderGoals();
  return c;
}

function renderGoals(): void {
  const activeKey = els.goal.value;
  const usdCop = state.usdCop;
  els.goalsTable.innerHTML = GOALS.map(goal => {
    const c = calculate({
      minutesDay: goal.minutesDay,
      daysWeek: goal.daysWeek,
      giftWeek: goal.giftWeek,
      usdCop
    });
    return `
      <tr class="${goal.key === activeKey ? 'current' : ''}">
        <td>${goal.name}</td>
        <td>${whole(goal.minutesDay)}</td>
        <td>${whole(goal.daysWeek)}</td>
        <td>${whole(goal.giftWeek)}</td>
        <td>${moneyCop(c.modelWeekCop)}</td>
        <td>${moneyUsd(c.modelWeekUsd)}</td>
        <td>${moneyCop(c.modelMonthCop)}</td>
        <td>${moneyUsd(c.modelMonthUsd)}</td>
      </tr>
    `;
  }).join('');
}

function applyGoal(goalKey: string): void {
  const goal = GOALS.find(g => g.key === goalKey) || GOALS[1];
  els.minutesDay.value = String(goal.minutesDay);
  els.daysWeek.value = String(goal.daysWeek);
  
  // Calculate daily gifted minutes based on weekly goal
  const giftsDay = Math.round(goal.giftWeek / goal.daysWeek);
  els.giftsDay.value = String(giftsDay);
  els.giftWeek.value = String(goal.giftWeek);
  
  renderSummary();
}

function saveFormState(): void {
  const stateToSave = {
    minutesDay: els.minutesDay.value,
    giftsDay: els.giftsDay.value,
    daysWeek: els.daysWeek.value,
    goal: els.goal.value
  };
  localStorage.setItem('modelo_agency_form_state', JSON.stringify(stateToSave));
}

function loadFormState(): boolean {
  const saved = localStorage.getItem('modelo_agency_form_state');
  if (saved) {
    try {
      const parsed = JSON.parse(saved);
      if (parsed) {
        els.minutesDay.value = parsed.minutesDay || '';
        els.giftsDay.value = parsed.giftsDay || '';
        els.daysWeek.value = parsed.daysWeek || '';
        els.goal.value = parsed.goal || 'base';
        return true;
      }
    } catch (e) {
      console.error('Error loading saved form state:', e);
    }
  }
  return false;
}

function resetForm(): void {
  localStorage.removeItem('modelo_agency_form_state');
  els.goal.value = 'base';
  applyGoal('base');
  showToast('Valores restablecidos');
}

function setupGoals(): void {
  els.goal.innerHTML = GOALS.map(g => `<option value="${g.key}">${g.name}</option>`).join('');
  els.goal.value = 'base';
  const loaded = loadFormState();
  if (!loaded) {
    applyGoal('base');
  }
}

function updateRateDisplay(): void {
  const source = state.liveRateSource === 'en vivo' ? 'en vivo' : 'referencia';
  els.rateDisplay.textContent = `${whole(state.usdCop)} COP por USD · ${source}`;
}

async function fetchLiveRate(): Promise<void> {
  try {
    const response = await fetch('https://open.er-api.com/v6/latest/USD', { cache: 'no-store' });
    if (!response.ok) throw new Error('No se pudo consultar la tasa');
    const data = (await response.json()) as { rates?: Record<string, number> };
    const rate = Number(data?.rates?.COP);
    if (!Number.isFinite(rate) || rate <= 0) throw new Error('Tasa inválida');
    state.usdCop = Math.round(rate);
    state.liveRateSource = 'en vivo';
  } catch (error) {
    state.usdCop = RATES.defaultUsdCop;
    state.liveRateSource = 'referencia base';
  }
  updateRateDisplay();
  renderSummary();
}

function exportImage(): void {
  // 1. Force a clean state refresh of the summary values
  renderSummary();

  // 2. Create the temporary wrapper to hold the container off-screen
  const wrapper = document.createElement('div');
  wrapper.style.position = 'absolute';
  wrapper.style.top = '-99999px';
  wrapper.style.left = '-99999px';
  wrapper.style.width = '1200px';
  wrapper.style.overflow = 'hidden';

  // 3. Create the temporary export container
  const tempContainer = document.createElement('div');
  tempContainer.className = 'export-template';

  // 4. Add Eyebrow
  const eyebrow = document.createElement('span');
  eyebrow.className = 'eyebrow';
  eyebrow.textContent = 'Simulador financiero';
  tempContainer.appendChild(eyebrow);

  // 5. Clone and add Title & Description from the webpage
  const heroContent = document.querySelector('.hero-content');
  if (heroContent) {
    const titleClone = heroContent.querySelector('h1')?.cloneNode(true);
    const descClone = heroContent.querySelector('.hero-desc')?.cloneNode(true);
    if (titleClone) tempContainer.appendChild(titleClone);
    if (descClone) tempContainer.appendChild(descClone);
  } else {
    const h1 = document.createElement('h1');
    h1.textContent = 'Simulador de Ganancias para Modelos';
    tempContainer.appendChild(h1);

    const desc = document.createElement('p');
    desc.className = 'hero-desc';
    desc.textContent = 'Estima cuánto podrías ganar como modelo según tus minutos de llamada y tus días trabajados por semana.';
    tempContainer.appendChild(desc);
  }

  // 6. Clone and add Rates Strip
  const ratesStrip = document.querySelector('.rates-strip');
  if (ratesStrip) {
    tempContainer.appendChild(ratesStrip.cloneNode(true));
  }

  // 7. Clone and add Summary Card
  const summaryCard = document.querySelector('#summaryCard');
  if (summaryCard) {
    tempContainer.appendChild(summaryCard.cloneNode(true));
  }

  // 8. Add footer notes matching the original canvas version
  const footer = document.createElement('div');
  footer.className = 'export-footer';
  footer.innerHTML = `
    <div><strong>Tasa de conversión:</strong> ${whole(state.usdCop)} COP por USD. <strong>Tarifas:</strong> USD 0.26 por min en llamada · USD 0.10 por min regalado.</div>
    <div>Cálculo mensual estimado sobre 4 semanas. Cifras de referencia para reclutamiento.</div>
  `;
  tempContainer.appendChild(footer);

  // 9. Put container inside wrapper and append wrapper to body
  wrapper.appendChild(tempContainer);
  document.body.appendChild(wrapper);

  // 10. Wait a small timeout to let the browser process stylesheet rules and layout
  window.setTimeout(() => {
    toPng(tempContainer, {
      width: 1200,
      cacheBust: true,
    })
    .then((dataUrl) => {
      // 11. Trigger download
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `resumen_modelo_${stamp}.png`;
      a.href = dataUrl;
      a.click();
      showToast('Imagen exportada');
    })
    .catch((error) => {
      console.error('Error al exportar la imagen:', error);
      showToast('Error al exportar');
    })
    .finally(() => {
      // 12. Clean up temporary wrapper
      wrapper.remove();
    });
  }, 150);
}


function exportPdf(): void {
  renderSummary();
  window.print();
}

function attachEvents(): void {
  els.goal.addEventListener('change', (e: Event) => {
    const target = e.target as HTMLSelectElement;
    applyGoal(target.value);
    saveFormState();
  });
  [els.minutesDay, els.giftsDay, els.daysWeek].forEach(el => {
    el.addEventListener('input', () => {
      renderSummary();
      saveFormState();
    });
  });
  els.exportImage.addEventListener('click', exportImage);
  els.exportPdf.addEventListener('click', exportPdf);
  els.resetBtn.addEventListener('click', resetForm);
}

setupGoals();
updateRateDisplay();
attachEvents();
renderSummary();
void fetchLiveRate();
